import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';

/**
 * Step Management store using Zustand
 * Manages current step information for navigation and API calls
 */

export interface StepInfo {
  id: string;
  path: string;
  label: string;
  index: number;
}

// State interface (data only)
interface StepManagementState extends BaseStoreState {
  currentStep: StepInfo | null;
  previousStep: StepInfo | null;
  nextStep: StepInfo | null;
  stepRoutes: string[];
  stepLabels: string[];
  completedSteps: string[];
  isNavigatingBack: boolean;
}

// Full store interface including actions
export interface StepManagementStore extends StepManagementState {
  setCurrentStep: (_stepPath: string) => void;
  initializeSteps: (_routes: string[], _labels: string[]) => void;
  getCurrentStepInfo: () => StepInfo | null;
  getPreviousStepInfo: () => StepInfo | null;
  getNextStepInfo: () => StepInfo | null;
  isLastStep: () => boolean;
  isFirstStep: () => boolean;
  markStepCompleted: (_stepPath: string) => void;
  unmarkStepCompleted: (_stepPath: string) => void;
  isStepCompleted: (_stepPath: string) => boolean;
  setIsNavigatingBack: (_isNavigating: boolean) => void;
  clearStore: () => void;
  resetSteps: () => void;
}

const createStepInfo = (
  path: string,
  routes: string[],
  labels: string[]
): StepInfo | null => {
  const index = routes.indexOf(path);
  if (index === -1) return null;

  return {
    id: path.replace('/', '') || 'company-type',
    path,
    label: labels[index] || 'Unknown Step',
    index,
  };
};

export const useStepManagementStore = createPersistedStore<StepManagementState>(
  'step-management',
  {
    currentStep: null,
    previousStep: null,
    nextStep: null,
    stepRoutes: [],
    stepLabels: [],
    completedSteps: [],
    isNavigatingBack: false,
  },
  (set, get) => ({
    // Actions
    setCurrentStep: (stepPath: string) => {
      const { stepRoutes, stepLabels } = get();

      if (stepRoutes.length === 0) {
        return;
      }

      const currentIndex = stepRoutes.indexOf(stepPath);
      if (currentIndex === -1) {
        return;
      }

      const currentStep = createStepInfo(stepPath, stepRoutes, stepLabels);
      const previousStep =
        currentIndex > 0
          ? createStepInfo(stepRoutes[currentIndex - 1], stepRoutes, stepLabels)
          : null;
      const nextStep =
        currentIndex < stepRoutes.length - 1
          ? createStepInfo(stepRoutes[currentIndex + 1], stepRoutes, stepLabels)
          : null;

      set({
        currentStep,
        previousStep,
        nextStep,
      });
    },

    initializeSteps: (routes: string[], labels: string[]) => {
      set({
        stepRoutes: routes,
        stepLabels: labels,
      });

      // If we have a current step path, update the step info
      const { currentStep } = get();
      if (currentStep?.path) {
        get().setCurrentStep(currentStep.path);
      }
    },

    getCurrentStepInfo: () => {
      return get().currentStep;
    },

    getPreviousStepInfo: () => {
      return get().previousStep;
    },

    getNextStepInfo: () => {
      return get().nextStep;
    },

    isLastStep: () => {
      const { currentStep, stepRoutes } = get();
      if (!currentStep || stepRoutes.length === 0) return false;
      return currentStep.index === stepRoutes.length - 1;
    },

    isFirstStep: () => {
      const { currentStep } = get();
      if (!currentStep) return true;
      return currentStep.index === 0;
    },

    markStepCompleted: (stepPath: string) => {
      const { completedSteps } = get();
      if (!completedSteps.includes(stepPath)) {
        set({ completedSteps: [...completedSteps, stepPath] });
      }
    },

    unmarkStepCompleted: (stepPath: string) => {
      const { completedSteps } = get();
      set({
        completedSteps: completedSteps.filter(
          (path: string) => path !== stepPath
        ),
      });
    },

    isStepCompleted: (stepPath: string) => {
      const { completedSteps } = get();
      return completedSteps.includes(stepPath);
    },

    setIsNavigatingBack: (isNavigating: boolean) => {
      set({ isNavigatingBack: isNavigating });
    },

    clearStore: () => {
      set({
        currentStep: null,
        previousStep: null,
        nextStep: null,
        stepRoutes: [],
        stepLabels: [],
        completedSteps: [],
        isNavigatingBack: false,
        loading: false,
        error: null,
      });
    },

    resetSteps: () => {
      set({
        currentStep: null,
        previousStep: null,
        nextStep: null,
        completedSteps: [],
        isNavigatingBack: false,
      });
    },
  }),
  {
    partialize: (state) => ({
      currentStep: state.currentStep,
      previousStep: state.previousStep,
      nextStep: state.nextStep,
      stepRoutes: state.stepRoutes,
      stepLabels: state.stepLabels,
      completedSteps: state.completedSteps,
      isNavigatingBack: state.isNavigatingBack,
    }),
  }
) as unknown as PersistedStoreHook<StepManagementStore>;

// Export store selectors for easy access
export const useCurrentStep = () =>
  useStepManagementStore((state) => state.currentStep);
export const usePreviousStep = () =>
  useStepManagementStore((state) => state.previousStep);
export const useNextStep = () =>
  useStepManagementStore((state) => state.nextStep);
export const useIsLastStep = () =>
  useStepManagementStore((state) => {
    const { currentStep, stepRoutes } = state;
    if (!currentStep || stepRoutes.length === 0) return false;
    return currentStep.index === stepRoutes.length - 1;
  });
export const useIsFirstStep = () =>
  useStepManagementStore((state) => {
    const { currentStep } = state;
    if (!currentStep) return true;
    return currentStep.index === 0;
  });
